"use client";

import { useCallback, useEffect, useState } from "react";
import { aiGateway } from "@/lib/emailCleaner/aiGateway";
import { listAndFetchMessages, performMessageAction } from "@/lib/emailCleaner/gmail";
import { useViewStack } from "@/lib/emailCleaner/useViewStack";
import type { NormalizedEmail } from "@/lib/emailCleaner/types";
import { Card, EmptyState, ScreenHeader } from "../Shared";
import EmailDetailPanel from "../EmailDetailPanel";

type View = { name: "list" } | { name: "detail"; id: string };

type Props = { accessToken: string; accountEmail: string; onExit: () => void };

export default function InboxTab({ accessToken, accountEmail, onExit }: Props) {
  const { view, depth, push, pop } = useViewStack<View>({ name: "list" });
  const back = depth > 1 ? pop : onExit;
  const [emails, setEmails] = useState<NormalizedEmail[]>([]);
  const [query, setQuery] = useState("in:inbox");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nlQuery, setNlQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [priorities, setPriorities] = useState<Record<string, { score: number; reason: string }>>({});
  const [ranking, setRanking] = useState(false);

  const load = useCallback(
    (q: string) => {
      setLoading(true);
      setError(null);
      listAndFetchMessages(accessToken, accountEmail, q, 40)
        .then(setEmails)
        .catch((err) => setError(err instanceof Error ? err.message : "Failed to load inbox"))
        .finally(() => setLoading(false));
    },
    [accessToken, accountEmail],
  );

  useEffect(() => {
    // Refetching the inbox whenever the query changes is the intended sync effect here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(query);
  }, [query, load]);

  async function handleSearch() {
    if (!nlQuery.trim()) return;
    setSearching(true);
    try {
      const { query: gmailQuery } = await aiGateway.search(nlQuery);
      setQuery(gmailQuery);
    } catch {
      setQuery(nlQuery);
    } finally {
      setSearching(false);
    }
  }

  async function handleRank() {
    setRanking(true);
    try {
      const { priorities: result } = await aiGateway.priority(emails);
      const map: Record<string, { score: number; reason: string }> = {};
      result.forEach((p) => (map[p.id] = { score: p.score, reason: p.reason }));
      setPriorities(map);
    } catch {
      // leave priorities empty on failure
    } finally {
      setRanking(false);
    }
  }

  async function handleAction(id: string, action: "trash" | "archive" | "markRead") {
    await performMessageAction(accessToken, id, action);
    setEmails((prev) => prev.filter((e) => e.id !== id));
  }

  if (view.name === "detail") {
    return (
      <div>
        <ScreenHeader title="Email Tools" onBack={back} />
        <EmailDetailPanel emails={emails} accessToken={accessToken} selectedId={view.id} selectedEmail={null} onSelect={() => {}} />
      </div>
    );
  }

  const sorted = Object.keys(priorities).length ? [...emails].sort((a, b) => (priorities[b.id]?.score ?? 0) - (priorities[a.id]?.score ?? 0)) : emails;

  return (
    <div>
      <ScreenHeader title="Inbox" onBack={back} />
      <div className="flex gap-2">
        <input
          value={nlQuery}
          onChange={(e) => setNlQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Search…"
          className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
        />
        <button type="button" onClick={handleSearch} disabled={searching} className="rounded-full bg-violet-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
          {searching ? "…" : "AI Search"}
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-slate-400 dark:text-slate-500">
        <span>
          Query: <code className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">{query}</code>
          {query !== "in:inbox" && (
            <button type="button" className="ml-2 underline" onClick={() => setQuery("in:inbox")}>
              reset
            </button>
          )}
        </span>
        <button type="button" onClick={handleRank} disabled={ranking || emails.length === 0} className="font-medium text-violet-600 disabled:opacity-60 dark:text-violet-400">
          {ranking ? "Ranking…" : "Rank by priority"}
        </button>
      </div>

      <div className="mt-4">
        {loading && <Card className="text-center text-sm text-slate-500 dark:text-slate-400">Loading…</Card>}
        {error && <Card className="text-sm text-red-600 dark:text-red-400">{error}</Card>}
        {!loading && !error && sorted.length === 0 && <EmptyState>No messages match this query.</EmptyState>}
        {!loading &&
          !error &&
          sorted.map((email) => (
            <Card key={email.id} className="mb-2">
              <button type="button" onClick={() => push({ name: "detail", id: email.id })} className="flex w-full items-start justify-between gap-3 text-left">
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-sm ${email.is_read ? "text-slate-600 dark:text-slate-300" : "font-semibold text-slate-900 dark:text-white"}`}>{email.from}</p>
                  <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                    {email.subject} — <span className="text-slate-400 dark:text-slate-500">{email.snippet}</span>
                  </p>
                </div>
                {priorities[email.id] && (
                  <span title={priorities[email.id].reason} className="shrink-0 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-600 dark:bg-violet-500/10 dark:text-violet-400">
                    {priorities[email.id].score}
                  </span>
                )}
              </button>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => handleAction(email.id, "archive")}
                  className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400"
                >
                  Archive
                </button>
                <button
                  type="button"
                  onClick={() => handleAction(email.id, "trash")}
                  className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-500 hover:border-red-300 hover:text-red-600 dark:border-slate-700 dark:text-slate-400"
                >
                  Trash
                </button>
              </div>
            </Card>
          ))}
      </div>
    </div>
  );
}
